<template>
  <BModal
    id="modal-setup-notification"
    ref="modal-setup-notification"
    no-stacking
    centered
    hide-footer
    hide-header
    hide-header-close
    no-close-on-backdrop
    no-close-on-esc
    size="md"
    @hidden="resetForm"
  >
    <ValidationObserver #default="{ invalid }">
      <BRow class="justify-center my-1 flex flex-column gap-3">
        <!-- Title -->
        <BCol class="text-black text-2xl text-center font-semibold">
          Notifikasi
        </BCol>

        <!-- Radio Group -->
        <BCol>
          <div class="d-flex gap-5">
            <BFormRadio
              v-model="selectedChannel"
              value="email"
              @change="onChannelChange"
            >
              Email
            </BFormRadio>
            <BFormRadio
              v-model="selectedChannel"
              value="telegram"
              @change="onChannelChange"
            >
              Telegram
            </BFormRadio>
          </div>
        </BCol>

        <!-- Badge Info -->
        <BCol>
          <div
            class="bg-[#EBF6FF] rounded p-[10px] text-sm text-black"
            style="border-radius: 6px"
          >
            <template v-if="selectedChannel === 'email'">
              Notifikasi <strong><em>Decline Transaction</em></strong> akan
              dikirim ke Email kamu.
            </template>
            <template v-else>
              Notifikasi <strong><em>Decline Transaction</em></strong> akan
              dikirim ke Telegram kamu. Hubungkan akun kamu dengan Telegram.
            </template>
          </div>
        </BCol>

        <!-- Form Email -->
        <BCol v-if="selectedChannel === 'email'">
          <!-- Field Email -->
          <BFormGroup>
            <template #label>
              <div class="text-black font-semibold mb-[2px]">
                Email
              </div>
            </template>
            <ValidationProvider
              #default="{ errors }"
              name="Email"
              :rules="{ required: true, email: true }"
            >
              <div class="d-flex align-items-center gap-2">
                <BFormInput
                  v-model="emailInput"
                  placeholder="Input email kamu"
                  :disabled="otpSent"
                  :state="errors.length > 0 ? false : null"
                  @input="onEmailChange"
                />
                <!-- Tombol Kirim Verifikasi -->
                <BButton
                  v-if="isDifferentEmail"
                  variant="link"
                  class="p-0 text-nowrap"
                  :disabled="otpSent || loadingOtp"
                  style="font-size: 13px; white-space: nowrap"
                  @click="requestOtp"
                >
                  <BSpinner
                    v-if="loadingOtp"
                    small
                    class="mr-1"
                  />
                  <div class="flex gap-1 items-center">
                    <span class="text-blue-500 underline">{{
                      showOtpField ? 'Kirim Ulang' : 'Kirim Verifikasi'
                    }}</span>
                    <span
                      v-if="otpSent && countdown > 0"
                      class="text-muted d-block mt-[4px]"
                    >
                      {{ countdownDisplay }}
                    </span>
                  </div>
                </BButton>
              </div>

              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </BFormGroup>

          <!-- Field Kode Verifikasi -->
          <BFormGroup v-if="showOtpField">
            <template #label>
              <div class="text-black font-semibold mb-[2px]">
                Kode Verifikasi
              </div>
            </template>
            <ValidationProvider
              #default="{ errors }"
              name="Kode Verifikasi"
              :rules="{ required: true, digits: 6 }"
            >
              <div class="position-relative">
                <BFormInput
                  v-model="otpCode"
                  placeholder=""
                  maxlength="6"
                  :state="errors.length > 0 ? false : null"
                  :disabled="loadingVerifyOtp || otpVerified"
                  @input="onOtpInput"
                />
                <BSpinner
                  v-if="loadingVerifyOtp"
                  small
                  class="position-absolute"
                  style="right: 12px; top: 50%; transform: translateY(-50%)"
                />
              </div>
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>
          </BFormGroup>
        </BCol>

        <!-- Action Buttons -->
        <BCol>
          <BRow>
            <BCol cols="6">
              <BButton
                variant="outline-primary"
                block
                @click="onCancel"
              >
                Batal
              </BButton>
            </BCol>
            <BCol cols="6">
              <BButton
                :disabled="isConnectDisabled(invalid) || loadingSetup"
                :variant="
                  isConnectDisabled(invalid) || loadingSetup
                    ? 'secondary'
                    : 'primary'
                "
                block
                @click="onConnect"
              >
                <BSpinner
                  v-if="loadingSetup"
                  small
                  class="mr-1"
                />
                {{ connectButtonLabel }}
              </BButton>
            </BCol>
          </BRow>
        </BCol>
      </BRow>
    </ValidationObserver>
  </BModal>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { newAxiosIns } from '@/libs/axios'
import { required, email, digits } from '@validations'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
  },

  props: {
    notificationSetup: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      // vee-validate rules
      required,
      email,
      digits,

      // channel selection
      selectedChannel: 'email',

      // email form
      emailInput: '',

      // OTP state
      otpToken: '',
      otpCode: '',
      otpSent: false,
      showOtpField: false,
      otpVerified: false,

      // loading states
      loadingOtp: false,
      loadingVerifyOtp: false,
      loadingSetup: false,

      // countdown
      countdown: 0,
      countdownTimer: null,
    }
  },

  computed: {
    /**
     * Email stored in Vuex store (userData)
     */
    storedEmail() {
      return this.$store.state.auth.userData?.email || ''
    },

    /**
     * Whether the current email input differs from the stored email
     */
    isDifferentEmail() {
      return (
        this.emailInput.trim() !== ''
        && this.emailInput.trim() !== this.storedEmail.trim()
      )
    },

    /**
     * Formats the countdown timer as MM:SS
     */
    countdownDisplay() {
      const m = Math.floor(this.countdown / 60)
        .toString()
        .padStart(2, '0')
      const s = (this.countdown % 60).toString().padStart(2, '0')
      return `${m}:${s}`
    },

    /**
     * Whether the server's active channel is already telegram
     * Used to determine the connect button label
     */
    isAlreadyTelegram() {
      return (
        this.notificationSetup?.notification_channel === 'telegram'
        && this.notificationSetup?.notification_status === 'active'
      )
    },

    /**
     * Dynamic label for the connect button
     * - Telegram already connected → "Hubungkan Ulang"
     * - Otherwise → "Hubungkan"
     */
    connectButtonLabel() {
      if (this.selectedChannel === 'telegram' && this.isAlreadyTelegram) {
        return 'Hubungkan Ulang'
      }
      return 'Hubungkan'
    },
  },

  watch: {
    notificationSetup: {
      immediate: true,
      handler(val) {
        if (val) {
          this.selectedChannel = val.notification_channel || 'email'
        }
      },
    },
  },

  mounted() {
    this.emailInput = this.storedEmail
  },

  beforeDestroy() {
    // Clean up interval to prevent memory leak
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  },

  methods: {
    /**
     * Handles channel radio change — resets all OTP state
     */
    onChannelChange() {
      this.resetOtpState()
      if (this.selectedChannel === 'email') {
        this.emailInput = this.storedEmail
      }
    },

    /**
     * Handles email input change — resets OTP state
     */
    onEmailChange() {
      this.resetOtpState()
    },

    /**
     * Resets all OTP-related state
     */
    resetOtpState() {
      this.otpToken = ''
      this.otpCode = ''
      this.otpSent = false
      this.showOtpField = false
      this.otpVerified = false
      this.countdown = 0
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    /**
     * Resets the entire form to its initial state
     * Uses the active notification channel as the default if available
     */
    resetForm() {
      this.selectedChannel = this.notificationSetup?.notification_channel || 'email'
      this.emailInput = this.storedEmail
      this.loadingOtp = false
      this.loadingVerifyOtp = false
      this.loadingSetup = false
      this.resetOtpState()
    },

    /**
     * Determines whether the connect button should be disabled
     * @param {boolean} invalid - invalid flag from ValidationObserver
     */
    isConnectDisabled(invalid) {
      if (this.selectedChannel === 'telegram') {
        // Telegram has no form — button is always enabled
        return false
      }
      // Email: form must be valid
      if (invalid) return true
      // If email differs from stored, OTP must be verified first
      if (this.isDifferentEmail && !this.otpVerified) return true
      return false
    },

    /**
     * Requests an OTP sent to the provided email
     * POST /auth/api/v1/otp/request-otp/email
     */
    async requestOtp() {
      this.loadingOtp = true
      try {
        const { data } = await newAxiosIns.post(
          '/auth/api/v1/otp/request-otp/email',
          {
            purpose: 'email',
            email: this.emailInput,
            product: 'komcards',
          },
        )
        const { token, next_request_at } = data.data
        this.otpToken = token
        this.otpSent = true
        this.showOtpField = true
        this.startCountdown(next_request_at)
      } catch (e) {
        this.$toast_error({
          message:
            e.response?.data?.meta?.message
            || 'Failed to send OTP. Please try again.',
        })
      } finally {
        this.loadingOtp = false
      }
    },

    /**
     * Starts the resend countdown based on next_request_at
     * @param {string} nextRequestAt - ISO date string from the API response
     */
    startCountdown(nextRequestAt) {
      if (this.countdownTimer) clearInterval(this.countdownTimer)

      const target = new Date(nextRequestAt).getTime()

      this.countdownTimer = setInterval(() => {
        const now = Date.now()
        const remaining = Math.max(0, Math.floor((target - now) / 1000))
        this.countdown = remaining

        if (remaining === 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
          // Re-enable the resend button
          this.otpSent = false
        }
      }, 1000)
    },

    /**
     * Auto-triggers OTP verification when 6 digits are entered
     */
    async onOtpInput() {
      if (this.otpCode.length === 6) {
        await this.verifyOtp()
      }
    },

    /**
     * Verifies the entered OTP code
     * POST /auth/api/v1/otp/verify-otp
     */
    async verifyOtp() {
      this.loadingVerifyOtp = true
      try {
        await newAxiosIns.post('/auth/api/v1/otp/verify-otp', {
          token: btoa(`${this.otpToken}%email`),
          otp: this.otpCode,
        })
        this.otpVerified = true
      } catch (e) {
        const { response } = e

        this.otpCode = ''
        this.otpVerified = false

        if (response.data.meta.message === 'otp not valid') {
          this.$toast_error({ message: 'OTP Not Valid' })
          return
        }
        this.$toast_error({
          message: e.response?.data?.meta?.message,
        })
      } finally {
        this.loadingVerifyOtp = false
      }
    },

    /**
     * Handles the connect button click
     * - Email: POST notification_setup with email receiver
     * - Telegram: POST notification_setup then redirect to Telegram bot
     */
    async onConnect() {
      this.loadingSetup = true
      if (this.selectedChannel === 'telegram') {
        // Redirect to Telegram bot to link the account
        window.open(process.env.VUE_APP_KOMCARDS_BOT_URL, '_blank')
        return
      }

      try {
        const payload = {
          notification_channel: 'email',
          notification_receiver: this.emailInput,
        }

        await newAxiosIns.post('/komcards/api/v1/notification_setup', payload)

        this.$toast_success({
          message: 'Kamu berhasil menghubungkan Notifikasi',
        })
        this.$emit('connected', { channel: this.selectedChannel })
        this.$bvModal.hide('modal-setup-notification')
      } catch (e) {
        this.$toast_error({
          message: 'Gagal menghubungkan Notifikasi. Silakan coba lagi.',
        })
      } finally {
        this.loadingSetup = false
      }
    },

    /**
     * Handles the cancel button click
     * resetForm is called automatically via the @hidden event on BModal
     */
    onCancel() {
      this.$bvModal.hide('modal-setup-notification')
    },
  },
}
</script>
